/**
 * Hook que mantiene en vivo el set de menciones no leídas para el usuario actual.
 *
 * Devuelve:
 *  - `count`: número total de menciones pendientes (alimenta el badge del sidebar).
 *  - `conversationIds`: Set<string> de IDs de conversaciones con al menos una
 *    mención pendiente — usado para filtrar la lista cuando el bucket activo es
 *    `mentions`.
 *  - `markAsRead(convId)`: marca todas las menciones del usuario en esa
 *    conversación como leídas. Se llama al entrar a la conversación.
 *
 * Implementación: una sola query inicial sobre `wa_message_mentions` y
 * suscripción realtime a INSERT/UPDATE para reflejar cambios sin refetch.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { logError } from '@/lib/logger';

interface MentionRow {
  id: string;
  conversation_id: string;
  read_at: string | null;
}

interface State {
  count: number;
  conversationIds: Set<string>;
  markAsRead: (conversationId: string) => Promise<void>;
}

export function useWaUnreadMentions(
  tenantId: string | null | undefined,
  userId: string | null | undefined,
): State {
  // Estado canónico: Map<mention_id, conversation_id> de menciones pendientes.
  // Usamos un Map (no Set) para poder reflejar UPDATE de read_at sin recargar.
  const [pending, setPending] = useState<Map<string, string>>(new Map());
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  // Carga inicial.
  useEffect(() => {
    if (!tenantId || !userId) {
      setPending(new Map());
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('wa_message_mentions')
        .select('id, conversation_id, read_at')
        .eq('tenant_id', tenantId)
        .eq('mentioned_user_id', userId)
        .is('read_at', null);
      if (error) {
        logError('useWaUnreadMentions: fetch inicial falló', error);
        return;
      }
      if (cancelled) return;
      const next = new Map<string, string>();
      for (const row of (data ?? []) as MentionRow[]) {
        next.set(row.id, row.conversation_id);
      }
      setPending(next);
    })();
    return () => { cancelled = true; };
  }, [tenantId, userId]);

  // Realtime: INSERT (alguien me mencionó) + UPDATE (alguien marcó leída).
  useEffect(() => {
    if (!tenantId || !userId) return;
    const ch = supabase
      .channel(`wa-mentions-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'wa_message_mentions',
          filter: `mentioned_user_id=eq.${userId}`,
        },
        (payload) => {
          const row = payload.new as MentionRow;
          if (row.read_at) return; // ya viene leída (improbable)
          setPending((prev) => {
            if (prev.has(row.id)) return prev;
            const next = new Map(prev);
            next.set(row.id, row.conversation_id);
            return next;
          });
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'wa_message_mentions',
          filter: `mentioned_user_id=eq.${userId}`,
        },
        (payload) => {
          const row = payload.new as MentionRow;
          if (row.read_at) {
            // Mención leída — sacarla del pendiente.
            setPending((prev) => {
              if (!prev.has(row.id)) return prev;
              const next = new Map(prev);
              next.delete(row.id);
              return next;
            });
          }
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(ch);
    };
  }, [tenantId, userId]);

  const markAsRead = useCallback(async (conversationId: string) => {
    const uid = userIdRef.current;
    if (!uid || !tenantId) return;
    // Update directo: marca todas las menciones pendientes del user en esta conv.
    const nowIso = new Date().toISOString();
    const { error } = await supabase
      .from('wa_message_mentions')
      .update({ read_at: nowIso })
      .eq('mentioned_user_id', uid)
      .eq('conversation_id', conversationId)
      .is('read_at', null);
    if (error) {
      logError('useWaUnreadMentions: markAsRead falló', error);
      return;
    }
    // Optimistic local update — el realtime UPDATE también lo hará pero
    // queremos respuesta inmediata para el badge del sidebar.
    setPending((prev) => {
      let mutated = false;
      const next = new Map(prev);
      for (const [mid, cid] of prev) {
        if (cid === conversationId) {
          next.delete(mid);
          mutated = true;
        }
      }
      return mutated ? next : prev;
    });
  }, [tenantId]);

  // Set derivado de IDs de conversación con menciones pendientes.
  const conversationIds = new Set<string>();
  for (const cid of pending.values()) conversationIds.add(cid);

  return {
    count: pending.size,
    conversationIds,
    markAsRead,
  };
}
